'use client';

import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { motion } from 'motion/react';
import Link from 'next/link';
import * as Sentry from '@sentry/nextjs';
import React from 'react';
interface ErrorComponentProps {
  errorCode?: string;
  errorTitle?: string;
  errorMessage?: string;
  homeLink?: string;
}

export function ErrorComponent({
  errorCode = '404',
  errorTitle = 'Page Not Found',
  errorMessage = 'The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.',
  homeLink = '/',
}: ErrorComponentProps) {
  React.useEffect(() => {
    Sentry.captureException(new Error(`${errorCode} - ${errorMessage}`));
  }, [errorCode, errorMessage]);

  return (
    <>
      {/* Animated Background Elements */}
      <motion.div
        className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          x: [0, -40, 0],
          y: [0, 40, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

      {/* Floating SVG Art - Error/Warning Theme */}
      <motion.div
        className="absolute top-20 right-10 w-64 h-64 opacity-30 pointer-events-none z-0"
        animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Alert Triangle */}
          <path
            d="M 100 30 L 170 150 L 30 150 Z"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />
          <circle cx="100" cy="110" r="5" fill="#3b82f6" />
          <rect x="97" y="70" width="6" height="30" fill="#3b82f6" />

          {/* Secondary Circle with X */}
          <circle
            cx="140"
            cy="80"
            r="25"
            stroke="#3b82f6"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M 130 70 L 150 90 M 150 70 L 130 90"
            stroke="#3b82f6"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </motion.div>

      <motion.div
        className="absolute bottom-20 left-10 w-48 h-48 opacity-30 pointer-events-none z-0"
        animate={{
          y: [0, 15, 0],
          rotate: [0, -3, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Question Mark Circle */}
          <circle
            cx="100"
            cy="100"
            r="70"
            stroke="#3b82f6"
            strokeWidth="3"
            fill="none"
          />
          <path
            d="M 85 75 Q 85 60 100 60 Q 115 60 115 75 Q 115 85 100 90 L 100 105"
            stroke="#3b82f6"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
          <circle cx="100" cy="120" r="5" fill="#3b82f6" />
        </svg>
      </motion.div>

      <div className="flex items-center justify-center px-4 py-12 relative z-10 min-h-[calc(100vh-80px)]">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: 'spring' }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-linear-to-br from-red-500/20 to-orange-500/20 border-2 border-red-500/50 mb-6"
            >
              <AlertCircle className="w-12 h-12 text-red-500" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="mb-4">
                <span className="inline-block px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-500 mb-4">
                  Error &quot;{errorCode}&quot;
                </span>
              </div>
              <h1 className="mb-2 text-foreground">{errorTitle}</h1>
              <p className="text-foreground-secondary text-lg max-w-xl mx-auto">
                {errorMessage}
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card rounded-xl shadow-xl p-8 border border-card-border"
          >
            <h3 className="text-foreground mb-4">What can you do?</h3>

            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 p-4 bg-secondary rounded-lg">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-primary">1</span>
                </div>
                <div>
                  <p className="text-foreground mb-1">Check the URL</p>
                  <p className="text-foreground-secondary text-sm">
                    Make sure the web address is spelled correctly
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-secondary rounded-lg">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-primary">2</span>
                </div>
                <div>
                  <p className="text-foreground mb-1">Go back to homepage</p>
                  <p className="text-foreground-secondary text-sm">
                    Start fresh from our main page
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-secondary rounded-lg">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-primary">3</span>
                </div>
                <div>
                  <p className="text-foreground mb-1">Contact support</p>
                  <p className="text-foreground-secondary text-sm">
                    If you believe this is a mistake, let us know
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                href={homeLink}
                className="flex-1 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2"
              >
                <Home className="w-5 h-5" />
                Go Home
              </Link>

              <button
                onClick={() => window.location.reload()}
                className="flex-1 py-3 bg-secondary text-foreground rounded-lg hover:bg-background-secondary transition-colors border border-card-border flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Refresh Page
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-card-border">
              <p className="text-foreground-secondary text-center text-sm">
                Need help? Visit our{' '}
                <Link href="/contact" className="text-primary hover:opacity-80">
                  Contact Page
                </Link>{' '}
                or check our{' '}
                <Link href="/about" className="text-primary hover:opacity-80">
                  About Us
                </Link>
              </p>
            </div>
          </motion.div>

          <div className="mt-6 text-center text-foreground-secondary">
            <p className="text-sm">
              Error Reference: {errorCode} - {new Date().toISOString()}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
